/**
 * Live gate data for screens: the village roster (typed), this phone's sessions, its gate commands
 * and the open delivery draft. Everything reads IndexedDB through useLiveQuery, so screens update
 * as commands are made and sync results arrive.
 */
import { useLiveQuery } from 'dexie-react-hooks'
import { useMemo } from 'react'
import { getDb, getMeta, openDrafts, readableOutbox, roster, type OutboxRecord } from '@/offline'
import type { DraftRecord } from '@/offline'
import { DRAFT_KIND, type DeliveryDraft } from '../model/delivery'
import { usedCodes } from '../model/day'
import { listSessions, openAppSession, openPaperSession, type GateSession } from './session'

const GATE_TYPES = [
  'open_session',
  'confirm_roll',
  'commit_delivery',
  'void_delivery',
  'void_tag',
  'retag',
  'spoil_slip',
  'close_session',
] as const

export function useGateRoster() {
  return useLiveQuery(async () => {
    const [
      farmers,
      rateCards,
      sackTypes,
      rolls,
      team,
      equipment,
      receiptBooks,
      config,
      downloadedAt,
      version,
    ] = await Promise.all([
      roster.farmers(),
      roster.rateCards(),
      roster.sackTypes(),
      roster.rolls(),
      roster.team(),
      roster.equipment(),
      roster.receiptBooks(),
      roster.gateConfig(),
      getMeta('roster_downloaded_at'),
      getMeta('roster_version'),
    ])
    return {
      farmers,
      rateCards,
      sackTypes: sackTypes.filter((s) => s.active !== false),
      rolls,
      team,
      equipment,
      receiptBooks,
      config,
      downloadedAt: downloadedAt ?? null,
      version: version ?? null,
    }
  }, [])
}
export type GateRoster = NonNullable<ReturnType<typeof useGateRoster>>

/** Gate commands on this phone, oldest first. */
export function useGateOutbox(): OutboxRecord[] | undefined {
  return useLiveQuery(async () => {
    const db = getDb()
    return readableOutbox(
      await db.outbox
        .where('type')
        .anyOf([...GATE_TYPES])
        .sortBy('seq'),
      db,
    )
  }, [])
}

export function useSessions(): GateSession[] | undefined {
  return useLiveQuery(() => listSessions(), [])
}

export function useCurrentSessions() {
  const sessions = useSessions()
  return useMemo(
    () => ({
      loaded: sessions !== undefined,
      sessions: sessions ?? [],
      app: sessions ? openAppSession(sessions) : null,
      paper: sessions ? openPaperSession(sessions) : null,
    }),
    [sessions],
  )
}

/** Open delivery drafts on this phone (V1: any number of farmers open at once; paper: one). */
export function useDeliveryDrafts(): DraftRecord<DeliveryDraft>[] | undefined {
  return useLiveQuery(async () => {
    const db = getDb()
    return openDrafts<DeliveryDraft>(await db.drafts.where('kind').equals(DRAFT_KIND).toArray(), db)
  }, [])
}

export function useUsedCodes(records: readonly OutboxRecord[] | undefined) {
  return useMemo(() => usedCodes(records ?? []), [records])
}

/**
 * V1: accounts typed in with no internet and not yet answered by the server (farmer id → last 4
 * digits). The full number is opened only to take its last 4.
 */
export function useQueuedBanks(): ReadonlyMap<string, string> {
  const recs = useLiveQuery(async () => {
    const db = getDb()
    return readableOutbox(
      await db.outbox
        .where('type')
        .equals('add_bank_account')
        .and((r) => ['PENDING', 'SENDING', 'WAIT_FOR_PARENT'].includes(r.status))
        .toArray(),
      db,
    )
  }, [])
  return useMemo(() => {
    const m = new Map<string, string>()
    for (const r of recs ?? []) {
      const p = r.payload as { farmer_id?: string; account_number?: string } | null
      if (p?.farmer_id && p.account_number) m.set(p.farmer_id, p.account_number.slice(-4))
    }
    return m
  }, [recs])
}
